import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";
import ChecklistItemLines from "../quality/checklists/checklistItemLines";
import ProConfirmation from "../../utils/ProConfirmation";
import { ResourceLoading } from "../../utils/ResourceLoading";
import {
  createOfferChecklist,
  deleteOfferChecklist,
  fetchOfferChecklist,
  updateOfferChecklist,
} from "../../../api/offer";
import { fetchChecklistTypes } from "../../../api/quality/checklistType";
import "./offer-workspace.css";

const unwrapChecklist = (response) =>
  response?.checklist || response?.data?.checklist || response?.data || response;

const unwrapTypes = (response) => {
  const raw = response?.data?.data || response?.data || response || [];
  return Array.isArray(raw) ? raw : [];
};

export default function OfferChecklistTab({ offer }) {
  const { t } = useTranslation();
  const [checklist, setChecklist] = useState(offer?.checklist || null);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [checklistTypes, setChecklistTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState("");

  const loadChecklist = useCallback(async () => {
    if (!offer?.id) return;
    setLoading(true);
    const response = await fetchOfferChecklist(offer.id, {
      fields: "id,title,description,status,is_items_editable,items.*",
    });
    const row = unwrapChecklist(response);
    if (response?.status === 403) {
      setUnavailable(true);
    } else if (response?.missing || response?.status === 404) {
      setChecklist(null);
      setUnavailable(false);
    } else if (response?.error || response?.errors) {
      toast.error(t("offers.checklist.load_error"));
    } else {
      setChecklist(row?.id ? { ...row, items: row.items || [] } : null);
      setUnavailable(false);
    }
    setLoading(false);
  }, [offer?.id, t]);

  const loadTypes = useCallback(async () => {
    setTypesLoading(true);
    try {
      const response = await fetchChecklistTypes({
        per_page: 200,
        fields: "id,title,name",
      });
      setChecklistTypes(unwrapTypes(response));
    } catch (_err) {
      setChecklistTypes([]);
    } finally {
      setTypesLoading(false);
    }
  }, []);

  useEffect(() => {
    setChecklist(offer?.checklist || null);
    loadChecklist();
  }, [offer?.checklist, loadChecklist]);

  useEffect(() => {
    if (!checklist?.id) {
      loadTypes();
    }
  }, [checklist?.id, loadTypes]);

  const createChecklist = async () => {
    setLoading(true);
    const payload = { offer: {} };
    if (selectedTypeId) {
      payload.offer.checklist_type_id = Number(selectedTypeId);
    }
    const response = await createOfferChecklist(offer.id, payload);
    const row = unwrapChecklist(response);
    setLoading(false);
    if (response?.status === 403) {
      setUnavailable(true);
      return;
    }
    if (!row?.id || response?.error || response?.errors) {
      toast.error(t("offers.checklist.create_error"));
      return;
    }
    setChecklist({ ...row, items: row.items || [] });
    toast.success(t("offers.checklist.created"));
  };

  const persistItems = async (items) => {
    const response = await updateOfferChecklist(offer.id, {
      offer: { checklist: { items } },
    });
    const row = unwrapChecklist(response);
    if (response?.error || response?.errors || !row?.id) {
      toast.error(t("offers.checklist.save_error"));
      return;
    }
    setChecklist({ ...row, items: row.items || [] });
  };

  const deleteChecklist = async () => {
    const response = await deleteOfferChecklist(offer.id);
    if (response?.error || response?.errors) {
      toast.error(t("offers.checklist.delete_error"));
      return;
    }
    setChecklist(null);
    setDeleteOpen(false);
    toast.success(t("offers.checklist.deleted"));
  };

  if (!offer?.id) return null;
  if (loading && !checklist) return <ResourceLoading />;

  if (unavailable) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
        {t("offers.checklist.unavailable")}
      </div>
    );
  }

  if (!checklist?.id) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center">
        <ClipboardDocumentCheckIcon className="mx-auto h-10 w-10 text-slate-400" />
        <h2 className="mt-3 text-base font-semibold text-slate-900">
          {t("offers.checklist.empty_title")}
        </h2>
        <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
          {t("offers.checklist.empty_description")}
        </p>
        <div className="mx-auto mt-5 flex max-w-md flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-center">
          <label className="sr-only" htmlFor="offer-checklist-type">
            {t("offers.checklist.type_label")}
          </label>
          <select
            id="offer-checklist-type"
            value={selectedTypeId}
            onChange={(e) => setSelectedTypeId(e.target.value)}
            disabled={typesLoading || loading}
            className="offer-select sm:min-w-[220px]"
          >
            <option value="">
              {typesLoading
                ? t("offers.checklist.types_loading")
                : t("offers.checklist.type_blank")}
            </option>
            {checklistTypes.map((type) => (
              <option key={type.id} value={String(type.id)}>
                {type.title || type.name || `#${type.id}`}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={createChecklist}
            disabled={loading}
            className="rounded-lg bg-[#1463C7] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0B3A78] disabled:opacity-50"
          >
            {t("offers.checklist.create")}
          </button>
        </div>
        <p className="mx-auto mt-2 max-w-md text-xs text-slate-400">
          {t("offers.checklist.type_hint")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            {t("offers.checklist.title")}
          </h2>
          <p className="text-sm text-slate-500">
            {t("offers.checklist.intro")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setDeleteOpen(true)}
          className="rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
        >
          {t("offers.checklist.delete")}
        </button>
      </div>
      <ChecklistItemLines
        checklist={checklist}
        items={checklist.items}
        editable
        drag
        addRow
        deleteRow
        deviation={false}
        type="edit"
        disabled={checklist.is_items_editable === false}
        parentCallback={persistItems}
      />
      <ProConfirmation
        severity="confirmation"
        open={deleteOpen}
        message={t("offers.checklist.delete_confirm")}
        action="delete"
        module="offer"
        yesCallback={deleteChecklist}
        cancelCallback={() => setDeleteOpen(false)}
      />
    </div>
  );
}
